import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Pencil, Plus, Timer, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  KpiCard,
  Panel,
  DataTable,
  inr,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import {
  FieldGroup,
  NotesField,
  NumberField,
  SwitchField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toneClass } from "@/lib/admin/order-badges";
import {
  deleteDeliveryZone,
  listDeliveryZones,
  saveDeliveryZone,
} from "@/lib/admin/delivery.functions";
import { zoneSchema, type DeliveryZone, type ZoneInput } from "@/lib/admin/delivery.schemas";

export const Route = createFileRoute("/_admin/admin/delivery-zones")({
  head: () => ({
    meta: [
      { title: "Delivery Zones — Admin" },
      {
        name: "description",
        content:
          "Mumbai delivery zones with covered areas, pincodes, delivery charges and daily order limits.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DeliveryZonesPage,
});

function emptyForm(): ZoneInput {
  return {
    zone_name: "",
    areas_covered: [],
    pincodes: [],
    expected_delivery_minutes: 45,
    delivery_charge_paise: 0,
    min_order_paise: 0,
    max_orders_per_day: 100,
    is_active: true,
    notes: "",
  };
}

const toList = (raw: string) =>
  raw
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

function DeliveryZonesPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listDeliveryZones);
  const saveFn = useServerFn(saveDeliveryZone);
  const deleteFn = useServerFn(deleteDeliveryZone);

  const zones = useQuery({
    queryKey: ["admin", "delivery", "zones"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<DeliveryZone | "new" | null>(null);
  const [form, setForm] = useState<ZoneInput>(emptyForm());
  const [areasText, setAreasText] = useState("");
  const [pincodesText, setPincodesText] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<DeliveryZone | null>(null);

  useEffect(() => {
    if (editing === "new") {
      setForm(emptyForm());
      setAreasText("");
      setPincodesText("");
    } else if (editing) {
      setForm({
        zone_name: editing.zone_name,
        areas_covered: editing.areas_covered,
        pincodes: editing.pincodes,
        expected_delivery_minutes: editing.expected_delivery_minutes,
        delivery_charge_paise: editing.delivery_charge_paise,
        min_order_paise: editing.min_order_paise,
        max_orders_per_day: editing.max_orders_per_day,
        is_active: editing.is_active,
        notes: editing.notes,
      });
      setAreasText(editing.areas_covered.join(", "));
      setPincodesText(editing.pincodes.join(", "));
    }
    setErrors({});
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: ZoneInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Zone saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(e.message === "duplicate" ? "That zone already exists." : "Couldn't save zone."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Zone removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove zone."),
  });

  const rows = useMemo(() => zones.data ?? [], [zones.data]);
  const kpi = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.is_active).length;
    const pincodes = new Set(rows.flatMap((r) => r.pincodes)).size;
    const drivers = rows.reduce((sum, r) => sum + r.driver_count, 0);
    const avgEta = total
      ? Math.round(rows.reduce((s, r) => s + r.expected_delivery_minutes, 0) / total)
      : 0;
    const capacity = rows.reduce((s, r) => s + r.max_orders_per_day, 0);
    return { total, active, pincodes, drivers, avgEta, capacity };
  }, [rows]);

  const columns: Array<DataColumn<DeliveryZone>> = [
    {
      key: "zone",
      header: "Zone name",
      render: (r) => <span className="font-medium text-foreground">{r.zone_name}</span>,
      sortValue: (r) => r.zone_name,
      exportValue: (r) => r.zone_name,
    },
    {
      key: "areas",
      header: "Areas covered",
      render: (r) => (
        <div className="flex max-w-[26rem] flex-wrap gap-1">
          {r.areas_covered.slice(0, 6).map((a) => (
            <Badge key={a} variant="secondary" className="text-[10px]">
              {a}
            </Badge>
          ))}
          {r.areas_covered.length > 6 ? (
            <span className="text-[11px] text-muted-foreground">
              +{r.areas_covered.length - 6} more
            </span>
          ) : null}
        </div>
      ),
      exportValue: (r) => r.areas_covered.join(" | "),
    },
    {
      key: "pincodes",
      header: "Pincodes",
      render: (r) => <span className="font-mono text-xs">{r.pincodes.join(", ") || "—"}</span>,
      exportValue: (r) => r.pincodes.join(" "),
    },
    {
      key: "eta",
      header: "Expected time",
      align: "right",
      render: (r) => `${r.expected_delivery_minutes} min`,
      sortValue: (r) => r.expected_delivery_minutes,
      exportValue: (r) => r.expected_delivery_minutes,
    },
    {
      key: "charge",
      header: "Delivery charge",
      align: "right",
      render: (r) => inr(r.delivery_charge_paise / 100),
      sortValue: (r) => r.delivery_charge_paise,
      exportValue: (r) => r.delivery_charge_paise / 100,
    },
    {
      key: "min",
      header: "Min order",
      align: "right",
      render: (r) => inr(r.min_order_paise / 100),
      sortValue: (r) => r.min_order_paise,
      exportValue: (r) => r.min_order_paise / 100,
    },
    {
      key: "cap",
      header: "Max orders/day",
      align: "right",
      render: (r) => r.max_orders_per_day,
      sortValue: (r) => r.max_orders_per_day,
      exportValue: (r) => r.max_orders_per_day,
    },
    {
      key: "drivers",
      header: "Drivers",
      align: "right",
      render: (r) => r.driver_count,
      sortValue: (r) => r.driver_count,
      exportValue: (r) => r.driver_count,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge className={toneClass(r.is_active ? "success" : "neutral")}>
          {r.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
      sortValue: (r) => String(r.is_active),
      exportValue: (r) => (r.is_active ? "active" : "inactive"),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      ),
      exportValue: () => "",
    },
  ];

  const set = <K extends keyof ZoneInput>(key: K, value: ZoneInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = () => {
    const candidate: ZoneInput = {
      ...form,
      areas_covered: toList(areasText),
      pincodes: toList(pincodesText),
    };
    const parsed = zoneSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    save.mutate({
      id: editing === "new" || editing === null ? null : editing.id,
      patch: parsed.data,
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Delivery Zones"
        description="Mumbai serviceable zones. Drivers, shifts and dispatch capacity are planned against these zones."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Total zones" value={String(kpi.total)} tone="info" icon={MapPin} loading={zones.isLoading} />
        <KpiCard label="Active zones" value={String(kpi.active)} tone="success" icon={MapPin} loading={zones.isLoading} />
        <KpiCard label="Pincodes covered" value={String(kpi.pincodes)} tone="teal" icon={MapPin} loading={zones.isLoading} />
        <KpiCard label="Drivers assigned" value={String(kpi.drivers)} tone="purple" icon={Users} loading={zones.isLoading} />
        <KpiCard label="Average ETA" value={`${kpi.avgEta} min`} tone="warn" icon={Timer} loading={zones.isLoading} />
        <KpiCard label="Daily capacity" value={String(kpi.capacity)} tone="neutral" icon={Timer} loading={zones.isLoading} />
      </div>

      <Panel title="Zone master" subtitle={`${rows.length} zones`}>
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add zone
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={zones.isLoading}
          searchOn={(r) => `${r.zone_name} ${r.areas_covered.join(" ")} ${r.pincodes.join(" ")}`}
          pageSize={10}
          exportName="delivery-zones"
          importModule="delivery-zones"
          minWidth={900}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New delivery zone" : "Edit delivery zone"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <FieldGroup title="Zone details">
              <TextField
                label="Zone name"
                value={form.zone_name}
                onChange={(v) => set("zone_name", v)}
                error={errors["zone_name"]}
              />
              <TextField
                label="Areas covered"
                placeholder="Bandra West, Khar West"
                value={areasText}
                onChange={setAreasText}
                className="sm:col-span-2"
              />
              <TextField
                label="Pincodes"
                placeholder="400050, 400052"
                value={pincodesText}
                onChange={setPincodesText}
                className="sm:col-span-2"
              />
              <SwitchField
                label="Active"
                hint="Inactive zones are hidden from planning"
                checked={form.is_active}
                onChange={(v) => set("is_active", v)}
              />
            </FieldGroup>

            <FieldGroup title="Service rules">
              <NumberField
                label="Expected delivery time"
                suffix="min"
                value={form.expected_delivery_minutes}
                onChange={(v) => set("expected_delivery_minutes", Math.max(5, Math.round(v)))}
              />
              <NumberField
                label="Delivery charge"
                suffix="₹"
                value={Math.round(form.delivery_charge_paise / 100)}
                onChange={(v) => set("delivery_charge_paise", Math.max(0, Math.round(v * 100)))}
              />
              <NumberField
                label="Minimum order value"
                suffix="₹"
                value={Math.round(form.min_order_paise / 100)}
                onChange={(v) => set("min_order_paise", Math.max(0, Math.round(v * 100)))}
              />
              <NumberField
                label="Max orders per day"
                value={form.max_orders_per_day}
                onChange={(v) => set("max_orders_per_day", Math.max(0, Math.round(v)))}
              />
              <NotesField value={form.notes} onChange={(v) => set("notes", v)} />
            </FieldGroup>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save zone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove zone?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.zone_name} will be removed. Drivers assigned to it become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
